import { useSwitchOrganization } from '@auth/entities/account';
import { useOrganizationsList } from '@auth/entities/organization';
import { hasRole, role, useAuthData } from '@auth/features/auth';
import { Building2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import { Select, Text } from '@/shared/ui';
import css from './organization-switch.module.scss';

type OrganizationSwitchProps = {
  className?: string;
};

const SELECT_WRAPPER_STYLE = { width: '100%' } as const;

export const OrganizationSwitch = ({ className }: OrganizationSwitchProps) => {
  const { data: authData } = useAuthData();
  const canSwitchOrganization = hasRole(
    authData?.user.roles ?? [],
    role.system
  );
  const currentOrganizationId =
    authData?.organization?.id ?? authData?.user.organizationId ?? '';
  const { data: organizations, isLoading } = useOrganizationsList(
    canSwitchOrganization
  );
  const mutation = useSwitchOrganization();
  const [error, setError] = useState<string | null>(null);

  const options = useMemo(
    () =>
      (organizations ?? []).map((organization) => ({
        value: organization.id,
        label: organization.title,
      })),
    [organizations]
  );

  if (!canSwitchOrganization) return null;

  const handleChange = async (organizationId: string) => {
    if (!organizationId || organizationId === currentOrganizationId) return;
    setError(null);
    try {
      await mutation.mutateAsync({ organizationId });
      window.location.reload();
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : 'Unable to switch organization'
      );
    }
  };

  return (
    <div className={joinCss(css.root, className)}>
      <Select
        className={css.trigger}
        disabled={isLoading || mutation.isPending}
        dropdownMinWidth={260}
        leadingIcon={<Building2 className={css.icon} />}
        onChange={(event) => handleChange(event.target.value)}
        options={options}
        placeholder="Select Organization"
        searchable
        searchPlaceholder="Search organizations"
        size="compact"
        style={SELECT_WRAPPER_STYLE}
        usePortal
        value={currentOrganizationId}
      />
      {error && (
        <Text as="span" className={css.error} size="xs" variant="error">
          {error}
        </Text>
      )}
    </div>
  );
};
